// ProcessTimeline.js - New Zealand
import React from 'react';
import { motion } from 'framer-motion';
import { Clock } from 'lucide-react';

const ProcessTimeline = () => {
  const timelines = [
    {
      pathway: 'Skilled Migrant Category',
      stages: [
        { step: 'Lodge Your EOI', duration: '1-2 weeks', description: 'Complete the online Expression of Interest and attach the evidence behind it' },
        { step: 'Selection from the Pool', duration: 'Varies', description: 'Draws are made on points score, generally 2-4 weeks apart' },
        { step: 'Invitation to Apply (ITA)', duration: '4 months', description: 'You get 4 months from the invitation to assemble everything required' },
        { step: 'Lodge Residence Application', duration: 'Within 4 months', description: 'File the complete application together with all supporting evidence' },
        { step: 'Assessment by Immigration NZ', duration: '6-12 months', description: 'Immigration NZ works through the file and verifies what you supplied' },
        { step: 'Residence Visa Grant', duration: '1-2 weeks', description: 'The residence visa is granted and you can make your first entry to NZ' }
      ],
      totalTime: '8-18 months',
      color: 'from-blue-500 to-blue-600'
    },
    {
      pathway: 'Work to Residence',
      stages: [
        { step: 'Find an Accredited Employer', duration: 'Varies', description: 'Obtain a job offer from an employer accredited by Immigration NZ' },
        { step: 'Work Visa Application', duration: '2-4 months', description: 'Apply for the work visa, which runs for 30 months' },
        { step: 'Work in New Zealand', duration: '24 months', description: 'Complete at least 24 months of work with that accredited employer' },
        { step: 'Residence Application', duration: '1-2 months', description: 'Apply for residence once the work requirement has been met' },
        { step: 'Assessment by Immigration NZ', duration: '6-9 months', description: 'Immigration NZ assesses the residence application' },
        { step: 'Residence Visa Grant', duration: '1-2 weeks', description: 'Approval comes through and residence is granted' }
      ],
      totalTime: '30-40 months',
      color: 'from-green-500 to-green-600'
    },
    {
      pathway: 'Partnership Residence',
      stages: [
        { step: 'Gather Relationship Evidence', duration: '2-4 weeks', description: 'Pull together proof that the relationship is genuine and stable' },
        { step: 'Lodge Application', duration: '1-2 weeks', description: 'File the partnership residence application with the full document set' },
        { step: 'Initial Assessment', duration: '3-6 months', description: 'Immigration NZ reviews the file and may come back for more information' },
        { step: 'Interview (if required)', duration: 'Varies', description: 'Attend an interview where Immigration NZ asks for one' },
        { step: 'Full Assessment', duration: '12-18 months', description: 'The case is processed in full, verification checks included' },
        { step: 'Residence Visa Grant', duration: '1-2 weeks', description: 'Residence is granted, initially on a temporary basis for 2 years' }
      ],
      totalTime: '12-24 months',
      color: 'from-purple-500 to-purple-600'
    }
  ];

  return (
    <section className="py-16 bg-surface">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            What to Expect on Timing
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Below are the stages each category moves through, with the time each one usually takes, so you can plan around them.
          </p>
        </motion.div>

        <div className="space-y-12">
          {timelines.map((timeline, tIndex) => (
            <motion.div
              key={tIndex}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: tIndex * 0.2 }}
              className="bg-white rounded-lg shadow-sm p-6 md:p-8"
            >
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="text-xl font-semibold text-primary mb-2">
                    {timeline.pathway}
                  </h3>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Clock size={16} />
                    <span className="font-light">Total Processing Time: <strong>{timeline.totalTime}</strong></span>
                  </div>
                </div>
              </div>

              <div className="relative">
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                <div className="space-y-8">
                  {timeline.stages.map((stage, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: sIndex * 0.1 }}
                      className="relative pl-12"
                    >
                      <div className={`absolute left-0 w-8 h-8 rounded-full bg-gradient-to-br ${timeline.color} flex items-center justify-center text-white text-sm font-semibold`}>
                        {sIndex + 1}
                      </div>
                      <div className="bg-surface p-4 rounded-lg">
                        <div className="flex items-start justify-between mb-2">
                          <h4 className="text-base font-semibold text-primary">
                            {stage.step}
                          </h4>
                          <span className="text-xs font-light text-secondary bg-white px-3 py-1 rounded-full whitespace-nowrap ml-4">
                            {stage.duration}
                          </span>
                        </div>
                        <p className="text-gray-600 text-sm font-light leading-relaxed">
                          {stage.description}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 grid md:grid-cols-2 gap-6"
        >
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              What Affects the Wait
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>How complete and accurate your application is</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Your points total and demand for your occupation</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Case volumes and priorities at Immigration NZ</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Any extra verification your case attracts</span>
              </li>
            </ul>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
            <h4 className="text-base font-semibold text-primary mb-3">
              What We Do For You
            </h4>
            <ul className="space-y-2 text-sm text-gray-700 font-light">
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Push your SMC points as high as they will go</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Make sure the documentation is complete and correct</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Watch the pool draws and track where your file sits</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                <span>Answer Immigration NZ requests quickly</span>
              </li>
            </ul>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessTimeline;
